import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { squadApi } from "../api/squad.js";
import { RuleEditor } from "../components/RuleEditor.js";
import { playerApi } from "../api/player.js";
import { useStore } from "../store/useStore.js";
import { UnitDetailScreen } from "./UnitDetailScreen.js";
import { UnitAvatar } from "../components/UnitAvatar.js";
import { UNIT_DISPLAY, ROLE_COLOR, unitLabel } from "../lib/unitName.js";
import { PERK_BY_ID } from "../lib/perks.js";
import { MODULE_ICON } from "../lib/modules.js";
import { upgradeCost, MAX_UNIT_LEVEL } from "../lib/game.js";
import { PRESET_VALUES } from "../lib/presets.js";
function getActiveSynergies(units) {
    const roles = units.map(u => UNIT_DISPLAY[u.unitDefId]?.role ?? "unknown");
    const count = (r) => roles.filter(x => x === r).length;
    const strikers = count("striker");
    const tanks = count("tank");
    const hackers = count("hacker");
    const supports = count("support");
    const result = [];
    if (strikers >= 2)
        result.push({ icon: "⚡", name: "Strike Force", desc: `strikers +8 ATK`, color: "#ef4444" });
    if (tanks >= 2)
        result.push({ icon: "🛡", name: "Iron Bastion", desc: `tanks +18 HP`, color: "#f59e0b" });
    if (hackers >= 2)
        result.push({ icon: "🔗", name: "Neural Net", desc: `hackers skill CD −1`, color: "#8b5cf6" });
    if (tanks >= 1 && supports >= 1)
        result.push({ icon: "💚", name: "Frontline Support", desc: `tank +6 DEF · support +5 skill power`, color: "#22c55e" });
    if (new Set(roles).size === 1)
        result.push({ icon: "💥", name: "Mono Assault", desc: `all +10% ATK`, color: "#22d3ee" });
    return result;
}
export function SquadScreen() {
    const [squad, setSquad] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [upgradingDef, setUpgradingDef] = useState(null);
    const [detailUnit, setDetailUnit] = useState(null);
    const [editingSlot, setEditingSlot] = useState(null);
    const [editingRules, setEditingRules] = useState(false);
    const { player, setPlayer, setSquad: setStoreSquad, brainType, setBrainType, manualStrategy, setManualStrategy } = useStore();
    async function load() {
        try {
            const s = await squadApi.active();
            setSquad(s);
            setStoreSquad(s);
            setError(null);
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => { void load(); }, []);
    async function handlePreset(preset) {
        if (!squad)
            return;
        try {
            await squadApi.setPreset(preset);
            setSquad({ ...squad, preset });
        }
        catch (e) {
            alert(e.message);
        }
    }
    async function handleUpgrade(unitDefId, level) {
        const cost = upgradeCost(level);
        if (!player || player.credits < cost) {
            alert(`Not enough credits. Need ${cost}, have ${player?.credits ?? 0}`);
            return;
        }
        setUpgradingDef(unitDefId);
        try {
            await squadApi.upgrade(unitDefId);
            const [fresh] = await Promise.all([playerApi.me(), load()]);
            setPlayer(fresh);
            // Update detailUnit if open
            if (detailUnit?.unitDefId === unitDefId) {
                setDetailUnit(null); // close detail, reload will refresh
            }
        }
        catch (e) {
            alert(e.message);
        }
        finally {
            setUpgradingDef(null);
        }
    }
    async function handleSlotChange(slot, unitDefId) {
        if (!squad)
            return;
        // Build new slots: replace this slot with new unit, swap if unit already in another slot
        const current = squad.units.map(u => ({ slot: u.slot, unitDefId: u.unitDefId }));
        const existing = current.find(s => s.unitDefId === unitDefId && s.slot !== slot);
        const displaced = current.find(s => s.slot === slot);
        const newSlots = current.map(s => {
            if (s.slot === slot)
                return { slot, unitDefId };
            if (existing && s.slot === existing.slot && displaced)
                return { slot: s.slot, unitDefId: displaced.unitDefId };
            return s;
        });
        try {
            await squadApi.setSlots(newSlots);
            setEditingSlot(null);
            await load();
        }
        catch (e) {
            alert(e.message);
        }
    }
    if (loading)
        return _jsx(Center, { children: "Loading squad\u2026" });
    if (error || !squad)
        return _jsx(NoSquad, {});
    // Unit detail overlay
    if (detailUnit) {
        return (_jsx(UnitDetailScreen, { unit: detailUnit, credits: player?.credits ?? 0, upgrading: upgradingDef === detailUnit.unitDefId, onUpgrade: detailUnit.level < MAX_UNIT_LEVEL ? () => handleUpgrade(detailUnit.unitDefId, detailUnit.level) : undefined, onClose: () => setDetailUnit(null), onNicknameChange: async (unitDefId, nickname) => {
                await squadApi.renameUnit(unitDefId, nickname);
                setSquad(s => s ? { ...s, units: s.units.map(u => u.unitDefId === unitDefId ? { ...u, nickname } : u) } : s);
                setDetailUnit(d => d ? { ...d, nickname } : d);
            } }));
    }
    // Rule editor overlay
    if (editingRules) {
        return _jsx(RuleEditor, { onClose: () => setEditingRules(false) });
    }
    // Unit picker overlay
    if (editingSlot !== null) {
        return (_jsx(UnitPicker, { slot: editingSlot, allUnits: squad.allUnits, currentSlots: squad.units, onPick: (unitDefId) => handleSlotChange(editingSlot, unitDefId), onClose: () => setEditingSlot(null) }));
    }
    return (_jsxs("div", { style: { padding: 16, display: "flex", flexDirection: "column", gap: 16, overflowY: "auto", animation: "neonIn 0.35s ease-out" }, children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [_jsxs("div", { children: [_jsx("div", { style: { fontWeight: 700, fontSize: 16 }, children: squad.name }), _jsxs("div", { style: { color: "#64748b", fontSize: 11 }, children: ["Active Squad \u00B7 ", squad.units.length, " units"] })] }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [_jsx("button", { onClick: () => setEditingRules(true), style: {
                                    background: "#1e1e2e", border: "1px solid #7c3aed44", borderRadius: 8,
                                    color: "#a78bfa", fontSize: 11, fontWeight: 600, padding: "5px 10px",
                                    cursor: "pointer",
                                }, children: "\u26A1 Rules" }), player && (_jsxs("div", { style: { color: "#22d3ee", fontWeight: 600, fontSize: 14 }, children: ["\u25C8 ", player.credits] }))] })] }), _jsxs("div", { children: [_jsx("div", { style: { color: "#64748b", fontSize: 11, marginBottom: 8 }, children: "AI PRESET" }), _jsx("div", { style: { display: "flex", gap: 8 }, children: PRESET_VALUES.map((p) => (_jsx("button", { onClick: () => handlePreset(p), style: {
                                flex: 1, padding: "8px 4px", borderRadius: 8,
                                border: `1px solid ${squad.preset === p ? "#7c3aed" : "#1e293b"}`,
                                background: squad.preset === p ? "#7c3aed22" : "transparent",
                                color: squad.preset === p ? "#a78bfa" : "#64748b",
                                fontSize: 12, fontWeight: squad.preset === p ? 600 : 400,
                                cursor: "pointer", textTransform: "capitalize",
                            }, children: p }, p))) })] }), _jsx(BrainSelector, { brainType: brainType, manualStrategy: manualStrategy, onBrainType: setBrainType, onManualStrategy: setManualStrategy }), (() => {
                const synergies = getActiveSynergies(squad.units);
                return synergies.length > 0 ? (_jsxs("div", { children: [_jsx("div", { style: { color: "#64748b", fontSize: 11, marginBottom: 6 }, children: "SYNERGIES" }), _jsx("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: synergies.map(s => (_jsxs("div", { style: {
                                    background: `${s.color}0f`,
                                    border: `1px solid ${s.color}33`,
                                    borderRadius: 8, padding: "6px 10px",
                                    display: "flex", alignItems: "center", gap: 8,
                                }, children: [_jsx("span", { style: { fontSize: 14 }, children: s.icon }), _jsx("span", { style: { color: s.color, fontWeight: 600, fontSize: 12 }, children: s.name }), _jsx("span", { style: { color: "#475569", fontSize: 11 }, children: s.desc })] }, s.name))) })] })) : (_jsx("div", { style: { color: "#1e293b", fontSize: 11, textAlign: "center", padding: "4px 0" }, children: "No synergies active" }));
            })(), _jsxs("div", { children: [_jsx("div", { style: { color: "#64748b", fontSize: 11, marginBottom: 8 }, children: "SQUAD" }), _jsx("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: squad.units.map((unit) => (_jsx(SlotRow, { unit: unit, credits: player?.credits ?? 0, onViewDetail: () => setDetailUnit(unit), onEditSlot: () => setEditingSlot(unit.slot) }, unit.slot))) })] }), _jsxs("div", { children: [_jsx("div", { style: { color: "#64748b", fontSize: 11, marginBottom: 8 }, children: "ALL UNITS" }), _jsx("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: squad.allUnits.map((unit) => {
                            const inSquad = squad.units.some(u => u.unitDefId === unit.unitDefId);
                            const display = UNIT_DISPLAY[unit.unitDefId] ?? { name: unit.unitDefId, role: "unknown", roleLabel: "Unit", roleDesc: "" };
                            const color = ROLE_COLOR[display.role] ?? "#64748b";
                            return (_jsxs("div", { onClick: () => setDetailUnit({ ...unit, slot: squad.units.find(u => u.unitDefId === unit.unitDefId)?.slot ?? 0 }), style: {
                                    background: "#12121a",
                                    border: `1px solid ${inSquad ? color + "33" : "#1e1e2e"}`,
                                    borderRadius: 10,
                                    padding: "10px 12px",
                                    display: "flex", alignItems: "center", gap: 10,
                                    cursor: "pointer",
                                    opacity: inSquad ? 1 : 0.55,
                                }, children: [_jsx(UnitAvatar, { role: display.role, size: 24 }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { color: inSquad ? color : "#475569", fontWeight: 600, fontSize: 13 }, children: unit.nickname || unitLabel(unit.unitDefId, squad.units.find(u => u.unitDefId === unit.unitDefId)?.slot) }), _jsxs("div", { style: { color: "#475569", fontSize: 11 }, children: [_jsx("span", { style: { color: inSquad ? color : "#475569", fontWeight: 600 }, children: display.roleLabel ?? display.role }), display.roleDesc ? ` · ${display.roleDesc}` : "", inSquad ? _jsx("span", { style: { color: "#334155" }, children: " \u00B7 in squad" }) : ""] })] }), _jsx("div", { style: { color: "#334155", fontSize: 12 }, children: "\u203A" })] }, unit.unitDefId));
                        }) })] })] }));
}
// ─── Slot row ─────────────────────────────────────────────────────────────────
function SlotRow({ unit, credits, onViewDetail, onEditSlot }) {
    const display = UNIT_DISPLAY[unit.unitDefId] ?? { name: unit.unitDefId, role: "unknown", roleLabel: "Unit", roleDesc: "" };
    const color = ROLE_COLOR[display.role] ?? "#64748b";
    const perk = unit.perkId ? PERK_BY_ID[unit.perkId] : null;
    const modIcon = unit.moduleId ? MODULE_ICON[unit.moduleId] : null;
    const displayName = unit.nickname || unitLabel(unit.unitDefId, unit.slot);
    const maxed = unit.level >= MAX_UNIT_LEVEL;
    const cost = upgradeCost(unit.level);
    const canAfford = credits >= cost;
    return (_jsxs("div", { className: "tap-card", style: {
            background: "#12121a",
            border: `1px solid ${color}33`,
            borderRadius: 10,
            padding: "10px 12px",
            display: "flex", alignItems: "center", gap: 10,
            boxShadow: `0 0 8px ${color}18`,
        }, children: [_jsx("div", { style: {
                    width: 22, height: 22, borderRadius: 6,
                    background: "#0a0a0f", color: "#334155",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                }, children: unit.slot }), _jsx(UnitAvatar, { role: display.role, size: 28 }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, onClick: onViewDetail, children: [_jsxs("div", { style: { cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }, children: [_jsx("span", { style: { color, fontWeight: 600, fontSize: 13 }, children: displayName }), _jsx("span", { style: { color: "#334155", fontSize: 10 }, children: "\u203A" })] }), _jsxs("div", { style: { color: "#475569", fontSize: 11, marginTop: 1 }, children: [_jsx("span", { style: { color: color, fontWeight: 600 }, children: display.roleLabel ?? display.role }), " · ", "Lv", unit.level, " \u00B7 HP ", unit.stats.hp, " \u00B7 ATK ", unit.stats.attack] }), display.roleDesc && (_jsx("div", { style: { color: "#334155", fontSize: 10, marginTop: 1 }, children: display.roleDesc }))] }), _jsx("div", { onClick: onViewDetail, title: maxed ? "Max level" : `Upgrade to Lv${unit.level + 1}`, style: {
                    flexShrink: 0, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 3,
                    padding: "4px 7px", borderRadius: 7,
                    background: maxed ? "#0a0a0f" : canAfford ? "#22d3ee18" : "#0a0a0f",
                    border: `1px solid ${maxed ? "#1e293b" : canAfford ? "#22d3ee55" : "#1e293b"}`,
                    color: maxed ? "#334155" : canAfford ? "#22d3ee" : "#475569",
                    fontSize: 11, fontWeight: 700,
                }, children: maxed ? "MAX" : _jsxs(_Fragment, { children: ["\u2B06 ", cost, "\u25C8"] }) }), _jsxs("div", { style: { display: "flex", gap: 4, flexShrink: 0 }, children: [modIcon && (_jsx("span", { title: unit.moduleId ?? "", style: { fontSize: 15, opacity: 0.8 }, children: modIcon })), perk && (_jsx("span", { title: perk.name, style: { fontSize: 15, opacity: 0.8 }, children: perk.icon }))] }), _jsx("button", { onClick: onEditSlot, style: {
                    background: "#1e293b", border: "none", borderRadius: 6,
                    color: "#64748b", fontSize: 11, padding: "4px 8px",
                    cursor: "pointer",
                }, children: "swap" })] }));
}
// ─── Unit picker ──────────────────────────────────────────────────────────────
function UnitPicker({ slot, allUnits, currentSlots, onPick, onClose }) {
    const currentInSlot = currentSlots.find(s => s.slot === slot)?.unitDefId;
    return (_jsxs("div", { style: { padding: 16, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: [_jsx("button", { onClick: onClose, style: { background: "none", border: "none", color: "#64748b", fontSize: 13, cursor: "pointer", padding: 0 }, children: "\u2190 Cancel" }), _jsxs("span", { style: { color: "#475569", fontSize: 12 }, children: ["SLOT ", slot, " \u2014 PICK UNIT"] }), _jsx("div", { style: { width: 56 } })] }), _jsx("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: allUnits.map((unit) => {
                    const display = UNIT_DISPLAY[unit.unitDefId] ?? { name: unit.unitDefId, role: "unknown" };
                    const color = ROLE_COLOR[display.role] ?? "#64748b";
                    const isActive = unit.unitDefId === currentInSlot;
                    const inOtherSlot = currentSlots.find(s => s.unitDefId === unit.unitDefId && s.slot !== slot);
                    return (_jsxs("div", { onClick: () => onPick(unit.unitDefId), style: {
                            background: isActive ? `${color}18` : "#12121a",
                            border: `1px solid ${isActive ? color + "66" : "#1e293b"}`,
                            borderRadius: 12, padding: "12px 14px",
                            display: "flex", alignItems: "center", gap: 12,
                            cursor: "pointer",
                        }, children: [_jsx(UnitAvatar, { role: display.role, size: 32 }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { color: isActive ? color : "#e2e8f0", fontWeight: 600, fontSize: 14 }, children: unitLabel(unit.unitDefId) }), _jsxs("div", { style: { color: "#475569", fontSize: 11 }, children: ["Lv", unit.level, " \u00B7 ", display.role, inOtherSlot ? ` · in slot ${inOtherSlot.slot} (will swap)` : ""] })] }), isActive && (_jsx("div", { style: { color, fontSize: 12, fontWeight: 600 }, children: "\u2713 current" }))] }, unit.unitDefId));
                }) })] }));
}
// ─── Helpers ──────────────────────────────────────────────────────────────────
function Center({ children }) {
    return (_jsx("div", { style: { display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#64748b" }, children: children }));
}
function NoSquad() {
    const { setPlayer } = useStore();
    const [loading, setLoading] = useState(false);
    async function handleSetup() {
        setLoading(true);
        try {
            await playerApi.onboarding();
        }
        catch (e) {
            const msg = e.message ?? "";
            // "Already onboarded" means player exists — skip and reload
            if (!msg.toLowerCase().includes("already")) {
                alert(msg);
                setLoading(false);
                return;
            }
        }
        try {
            const fresh = await playerApi.me();
            setPlayer(fresh);
            window.location.reload();
        }
        catch (e) {
            alert(e.message);
            setLoading(false);
        }
    }
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: 24 }, children: [_jsx("div", { style: { color: "#64748b", fontSize: 14, textAlign: "center" }, children: "No squad yet" }), _jsx("button", { onClick: handleSetup, disabled: loading, style: {
                    background: "#7c3aed", color: "#fff", border: "none", borderRadius: 12,
                    padding: "12px 32px", fontSize: 15, fontWeight: 700,
                    cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1,
                }, children: loading ? "Setting up…" : "Setup Squad" })] }));
}
// ─── Brain selector ───────────────────────────────────────────────────────────
const TARGET_LABELS = {
    lowest_hp: "Lowest HP",
    highest_atk: "Highest ATK",
    highest_speed: "Fastest",
    random: "Random",
};
function BrainSelector({ brainType, manualStrategy, onBrainType, onManualStrategy }) {
    return (_jsxs("div", { children: [_jsx("div", { style: { color: "#64748b", fontSize: 11, marginBottom: 8 }, children: "BRAIN" }), _jsxs("div", { style: { display: "flex", gap: 8, marginBottom: brainType === "manual" ? 10 : 0 }, children: [_jsx("button", { onClick: () => onBrainType("preset"), style: {
                            flex: 1, padding: "8px 4px", borderRadius: 8,
                            border: `1px solid ${brainType === "preset" ? "#22d3ee" : "#1e293b"}`,
                            background: brainType === "preset" ? "#22d3ee22" : "transparent",
                            color: brainType === "preset" ? "#22d3ee" : "#64748b",
                            fontSize: 12, fontWeight: brainType === "preset" ? 600 : 400,
                            cursor: "pointer",
                        }, children: "\uD83E\uDD16 Preset" }), _jsx("button", { onClick: () => onBrainType("manual"), style: {
                            flex: 1, padding: "8px 4px", borderRadius: 8,
                            border: `1px solid ${brainType === "manual" ? "#f59e0b" : "#1e293b"}`,
                            background: brainType === "manual" ? "#f59e0b22" : "transparent",
                            color: brainType === "manual" ? "#f59e0b" : "#64748b",
                            fontSize: 12, fontWeight: brainType === "manual" ? 600 : 400,
                            cursor: "pointer",
                        }, children: "\u2699\uFE0F Manual" }), _jsxs("button", { disabled: true, style: {
                            flex: 1, padding: "8px 4px", borderRadius: 8,
                            border: "1px solid #1e293b",
                            background: "transparent",
                            color: "#334155",
                            fontSize: 12, cursor: "default",
                            position: "relative",
                        }, children: ["\u2728 LLM", _jsx("span", { style: {
                                    position: "absolute", top: -6, right: -4,
                                    background: "#7c3aed", color: "#fff",
                                    fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 4,
                                }, children: "M5" })] })] }), brainType === "manual" && (_jsxs("div", { style: {
                    background: "#12121a",
                    border: "1px solid #f59e0b33",
                    borderRadius: 10,
                    padding: "12px 14px",
                    display: "flex", flexDirection: "column", gap: 12,
                }, children: [_jsxs("div", { children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", marginBottom: 6 }, children: [_jsx("span", { style: { color: "#94a3b8", fontSize: 11 }, children: "Aggression" }), _jsxs("span", { style: { color: "#f59e0b", fontSize: 11, fontWeight: 600 }, children: [manualStrategy.aggression, "%"] })] }), _jsx("input", { type: "range", min: 0, max: 100, step: 10, value: manualStrategy.aggression, onChange: (e) => onManualStrategy({ aggression: Number(e.target.value) }), style: { width: "100%", accentColor: "#f59e0b" } }), _jsxs("div", { style: { display: "flex", justifyContent: "space-between", marginTop: 2 }, children: [_jsx("span", { style: { color: "#334155", fontSize: 10 }, children: "\uD83D\uDC22 Turtle" }), _jsx("span", { style: { color: "#334155", fontSize: 10 }, children: "\u26A1 Rush" })] })] }), _jsxs("div", { children: [_jsx("div", { style: { color: "#94a3b8", fontSize: 11, marginBottom: 6 }, children: "Target Priority" }), _jsx("div", { style: { display: "flex", gap: 6, flexWrap: "wrap" }, children: Object.keys(TARGET_LABELS).map((tp) => (_jsx("button", { onClick: () => onManualStrategy({ targetPriority: tp }), style: {
                                        padding: "4px 10px", borderRadius: 6,
                                        border: `1px solid ${manualStrategy.targetPriority === tp ? "#f59e0b" : "#1e293b"}`,
                                        background: manualStrategy.targetPriority === tp ? "#f59e0b22" : "transparent",
                                        color: manualStrategy.targetPriority === tp ? "#f59e0b" : "#475569",
                                        fontSize: 11, cursor: "pointer",
                                    }, children: TARGET_LABELS[tp] }, tp))) })] }), _jsxs("div", { onClick: () => onManualStrategy({ focusFire: !manualStrategy.focusFire }), style: {
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            cursor: "pointer",
                        }, children: [_jsxs("div", { children: [_jsx("div", { style: { color: "#94a3b8", fontSize: 11 }, children: "Focus Fire" }), _jsx("div", { style: { color: "#475569", fontSize: 10 }, children: "All units target the same enemy" })] }), _jsx("div", { style: {
                                    width: 36, height: 20, borderRadius: 10,
                                    background: manualStrategy.focusFire ? "#f59e0b" : "#1e293b",
                                    position: "relative", transition: "background 0.2s",
                                    flexShrink: 0,
                                }, children: _jsx("div", { style: {
                                        position: "absolute", top: 2,
                                        left: manualStrategy.focusFire ? 18 : 2,
                                        width: 16, height: 16, borderRadius: 8,
                                        background: "#fff", transition: "left 0.2s",
                                    } }) })] })] })), brainType === "preset" && (_jsx("div", { style: { color: "#334155", fontSize: 11, marginTop: 4 }, children: "Uses the AI Preset above (Balanced / Aggressive / Defensive)" }))] }));
}
